import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getReportByIdWithToken, getChecksByVisitIdWithToken } from '../api/reportApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';


// 添加Markdown样式CSS
const markdownStyles = `
  .markdown-content {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    text-align: left;
  }
  
  .markdown-content h1 {
    color: #14253a;
    font-size: 1.8em;
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    padding-bottom: 0.3em;
    border-bottom: 1px solid #eaecef;
    text-align: left;
  }
  
  .markdown-content h2 {
    color: #43d6c5;
    font-size: 1.5em;
    margin-top: 1.2em;
    margin-bottom: 0.6em;
    text-align: left;
  }
  
  .markdown-content h3 {
    color: #2980b9;
    font-size: 1.3em;
    margin-top: 1em;
    margin-bottom: 0.5em;
    text-align: left;
  }
  
  .markdown-content p {
    margin-top: 0.5em;
    margin-bottom: 1em;
    text-align: left;
  }
  
  .markdown-content ul, .markdown-content ol {
    margin-top: 0.5em;
    margin-bottom: 1em;
    padding-left: 2em;
    text-align: left;
  }
  
  .markdown-content li {
    margin-bottom: 0.3em;
    text-align: left;
  }
  
  .markdown-content img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1.5em 0;
    border-radius: 4px;
  }
  
  .markdown-content blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    border-left: 4px solid #43d6c5;
    background-color: #f8f9fa;
    color: #6d6459;
  }
  
  .markdown-content code {
    font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
    background-color: #f6f8fa;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-size: 0.9em;
  }
  
  .markdown-content pre {
    background-color: #f6f8fa;
    padding: 1em;
    overflow-x: auto;
    border-radius: 4px;
    margin: 1em 0;
  }
  
  .markdown-content pre code {
    background-color: transparent;
    padding: 0;
    font-size: 0.9em;
    color: #333;
  }
  
  .markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
    overflow-x: auto;
    display: block;
  }
  
  .markdown-content table th {
    background-color: #f8f9fa;
    border: 1px solid #dfe2e5;
    padding: 0.5em 1em;
    font-weight: 600;
  }
  
  .markdown-content table td {
    border: 1px solid #dfe2e5;
    padding: 0.5em 1em;
  }
`;

// 检查类型名称映射
const checkTypeNames = {
  'blood': '血液检查',
  'urine': '尿液检查',
  'ct': 'CT扫描',
  'xray': 'X光检查',
  'ultrasound': '超声波检查',
  'ecg': '心电图',
  'mri': '核磁共振',
  'pathology': '病理检查',
  'endoscopy': '内窥镜检查',
  'common': '常规检查'
};

const ClinicReportView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [report, setReport] = useState(null);
  const [reportContent, setReportContent] = useState('');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checksLoading, setChecksLoading] = useState(true);
  const [error, setError] = useState('');
  const [checksError, setChecksError] = useState('');
  
  useEffect(() => {
    const fetchReport = async () => {
      if (!token) {
        setError(t('reports.missingAccessToken'));
        setLoading(false);
        return;
      }
      
      try {
        const data = await getReportByIdWithToken(id, token);
        setReport(data);
        
        // 验证报告状态是否为已审核通过
        if (data.status !== 'approved') {
          setError(t('reports.notApprovedReport'));
          setLoading(false);
          return;
        }
        
        // 处理报告内容
        if (typeof data.content === 'string') {
          setReportContent(data.content);
        } else if (data.content) {
          setReportContent(JSON.stringify(data.content, null, 2));
        } else {
          setReportContent('');
        }
        
        // 获取相关的检查项
        if (data.visit_id) {
          fetchChecks(data.visit_id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('获取报告详情失败:', error);
        setError(t('reports.fetchReportError') + ': ' + (error.response?.data?.error || t('reports.unknownErrorOrInvalidAuth')));
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [id, token]);
  
  // 获取检查项列表
  const fetchChecks = async (visitId) => {
    if (!token) {
      setChecksError(t('reports.missingTokenForChecks'));
      setChecksLoading(false);
      return;
    }
    
    try {
      setChecksLoading(true);
      const data = await getChecksByVisitIdWithToken(visitId, token);
      setChecks(data || []);
      setChecksError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的检查项失败:`, error);
      setChecksError(t('reports.fetchChecksError') + ': ' + (error.response?.data?.error || error.message || t('common.unknownError')));
    } finally {
      setChecksLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN');
  };
  
  // 获取检查类型名称
  const getCheckTypeName = (type) => {
    return t(`checkTypes.${type}`) || type || t('reports.unknownType');
  };
  
  // 处理检查项详情显示/隐藏
  const toggleCheckDetails = (index) => {
    const detailsElement = document.getElementById(`check-details-${index}`);
    if (detailsElement) {
      const isVisible = detailsElement.style.display === 'block';
      detailsElement.style.display = isVisible ? 'none' : 'block';
      return !isVisible;
    }
    return false;
  };
  
  // 渲染检查项元数据
  const renderCheckMetadata = (check) => {
    if (!check.metadata || (!Array.isArray(check.metadata) && typeof check.metadata !== 'object')) {
      return <p style={{ color: '#334255' }}>{t('reports.noMetadata')}</p>;
    }
    
    // 将metadata转换为统一的数组格式，支持处理旧数据格式
    let metadataItems = [];
    if (Array.isArray(check.metadata)) {
      metadataItems = check.metadata;
    } else if (typeof check.metadata === 'object') {
      metadataItems = Object.entries(check.metadata).map(([key, value]) => ({
        name: key,
        value: value
      }));
    }
    
    if (metadataItems.length === 0) {
      return <p style={{ color: '#334255' }}>{t('reports.noMetadata')}</p>;
    }
    
    return (
              <div className="check-metadata">
          <h4 style={{ color: '#14253a' }}>{t('reports.checkItemData')}</h4>
          <table className="paper-table">
            <thead>
              <tr>
                <th>{t('reports.name')}</th>
                <th>{t('reports.value')}</th>
              </tr>
            </thead>
          <tbody>
            {metadataItems.map((item, idx) => (
              <tr key={idx}>
                <td>{item.name_local || item.name}</td>
                <td>
                                        {item.value !== undefined && item.value !== null
                        ? <span>
                            {String(item.value)}
                            {item.unit ? ` ${item.unit}` : ''}
                            {item.reference_range ? ` (${t('reports.referenceRange')}: ${item.reference_range})` : ''}
                          </span>
                        : t('reports.noData')
                      }
                  {item.description_local || item.description
                    ? <div style={{ marginTop: '5px', color: '#556476', fontSize: '0.9em' }}>
                        {item.description_local || item.description}
                      </div>
                    : null
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading-spinner" style={{ 
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '4px solid rgba(0,0,0,.1)',
          borderRadius: '50%',
          borderTopColor: '#43d6c5',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p>{t('reports.loadingReportContent')}</p>
        
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container" style={{ padding: '30px' }}>
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#ffebee', 
          borderRadius: '5px', 
          color: '#d32f2f',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h2 style={{ color: '#c62828', marginTop: '0' }}>{t('reports.cannotGetReport')}</h2>
          <p>{error}</p>
          <p>{t('reports.contactAdminIfError')}</p>
        </div>
      </div>
    );
  }
  
  if (!report) {
    return (
      <div className="container" style={{ padding: '30px' }}>
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#e3f2fd', 
          borderRadius: '5px', 
          color: '#1565c0',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h2 style={{ color: '#0d47a1', marginTop: '0' }}>{t('reports.reportNotFound')}</h2>
          <p>{t('reports.confirmCorrectAccessLink')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ 
        backgroundColor: '#102033', 
        color: 'white', 
        padding: '15px 20px', 
        borderRadius: '5px 5px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: '0', fontSize: '1.5rem' }}>{t('reports.clinicReportView')}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>{t('reports.reportId')}: {report.id}</div>
        </div>
      </div>
      
      <div style={{ 
        border: '1px solid #ddd', 
        borderTop: 'none', 
        borderRadius: '0 0 5px 5px',
        backgroundColor: 'rgba(250, 245, 236, 0.94)',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
          <h2 style={{ color: '#14253a', margin: '0 0 15px 0', fontSize: '1.3rem' }}>{t('reports.reportInfo')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '10px' }}>
            <div>
              <strong>{t('reports.visitId')}:</strong> {report.visit_id}
            </div>
            <div>
              <strong>{t('reports.createdAt')}:</strong> {formatDate(report.created_at)}
            </div>
            <div>
              <strong>{t('reports.reviewedAt')}:</strong> {report.reviewed_at ? formatDate(report.reviewed_at) : t('reports.notReviewed')}
            </div>
            <div>
              <strong>{t('reports.reviewer')}:</strong> {report.reviewer_name || t('reports.notSpecified')}
            </div>
            <div>
              <strong>{t('reports.status')}:</strong> <span style={{ 
                display: 'inline-block',
                padding: '3px 8px',
                borderRadius: '3px',
                backgroundColor: '#2fa67b',
                color: 'white',
                fontSize: '0.8rem'
              }}>{t('reports.approved')}</span>
            </div>
          </div>
          
          {report.review_note && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <strong>{t('reports.reviewNote')}:</strong> {report.review_note}
            </div>
          )}
        </div>
        
        <div style={{ padding: '20px' }}>
          <h2 style={{ color: '#14253a', margin: '0 0 15px 0', fontSize: '1.3rem' }}>{t('reports.reportContent')}</h2>
          
          {reportContent ? (
            <div className="markdown-content markdown-content--paper" style={{ 
              backgroundColor: '#fff', 
              padding: '15px', 
              borderRadius: '4px',
              border: '1px solid #eee'
            }}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                  pre: ({node, ...props}) => <pre {...props} style={{margin: '1em 0'}} />,
                }}
              >
                {reportContent}
              </ReactMarkdown>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <p style={{ color: '#7f8c8d', margin: '0' }}>{t('reports.noContent')}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* 检查项列表 */}
      <div style={{ 
        border: '1px solid #ddd', 
        borderRadius: '5px',
        backgroundColor: 'rgba(250, 245, 236, 0.94)',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          backgroundColor: '#102033', 
          color: 'white', 
          padding: '10px 20px', 
          borderRadius: '5px 5px 0 0'
        }}>
          <h2 style={{ margin: '0', fontSize: '1.3rem' }}>{t('reports.checksList')}</h2>
        </div>
        
        <div style={{ padding: '20px' }}>
          {checksLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ 
                display: 'inline-block',
                width: '30px',
                height: '30px',
                border: '3px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#43d6c5',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '10px' }}>{t('reports.loadingChecks')}</p>
            </div>
          ) : checksError ? (
            <div style={{ color: 'red', marginBottom: '10px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
              {checksError}
            </div>
          ) : checks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <p style={{ color: '#7f8c8d', margin: '0' }}>{t('reports.noChecksData')}</p>
            </div>
          ) : (
            <div className="checks-list">
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                border: '1px solid #eee',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#ece7dc' }}>
                    <th style={{ padding: '12px 15px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '30%' }}>{t('reports.checkType')}</th>
                    <th style={{ padding: '12px 15px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '25%' }}>{t('reports.createdAt')}</th>
                    <th style={{ padding: '12px 15px', textAlign: 'left', borderBottom: '2px solid #ddd', width: '45%' }}>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((check, index) => (
                    <React.Fragment key={check.id || index}>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px 15px', fontWeight: 'bold', color: '#14253a' }}>
                          {getCheckTypeName(check.check_type)}
                        </td>
                        <td style={{ padding: '12px 15px', color: '#7f8c8d', fontSize: '0.9rem' }}>
                          {formatDate(check.created_at)}
                        </td>
                        <td style={{ padding: '12px 15px' }}>
                          <button 
                            onClick={() => toggleCheckDetails(index)}
                            style={{ 
                              backgroundColor: '#43d6c5',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.9rem'
                            }}
                          >
                            {t('reports.viewDetails')}
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3" style={{ padding: '0' }}>
                          <div 
                            id={`check-details-${index}`}
                            style={{ 
                              display: 'none',
                              padding: '15px',
                              backgroundColor: '#f8f9fa',
                              borderBottom: '1px solid #eee'
                            }}
                          >
                            {renderCheckMetadata(check)}
                            
                            {check.description && (
                              <div style={{ marginTop: '15px' }}>
                                <h4 style={{ color: '#14253a', margin: '0 0 10px 0', fontSize: '1rem' }}>{t('reports.description')}</h4>
                                <p style={{ margin: '0', padding: '10px', backgroundColor: 'rgba(250, 245, 236, 0.94)', borderRadius: '4px', border: '1px solid #eee' }}>
                                  {check.description}
                                </p>
                              </div>
                            )}
                            
                            {check.file_url && (
                              <div style={{ marginTop: '15px' }}>
                                <h4 style={{ color: '#14253a', margin: '0 0 10px 0', fontSize: '1rem' }}>附件</h4>
                                <div>
                                  <a 
                                    href={check.file_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'inline-block',
                                      padding: '8px 15px',
                                      backgroundColor: '#43d6c5',
                                      color: 'white',
                                      textDecoration: 'none',
                                      borderRadius: '4px',
                                      marginTop: '5px'
                                    }}
                                  >
                                    {t('reports.viewDownloadFile')}
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <div style={{ textAlign: 'center', marginTop: '30px', color: '#7f8c8d', fontSize: '0.9rem' }}>
        <div>© {new Date().getFullYear()} 诊所医疗系统 - 报告查看页面</div>
        <div style={{ marginTop: '5px' }}>如有问题，请联系医疗机构获取支持</div>
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        ${markdownStyles}
        
        body {
          background-color: #ece7dc;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
        }
        
        table th, table td {
          text-align: left;
          padding: 8px;
        }
        
        .check-metadata table {
          border: 1px solid rgba(20, 37, 58, 0.12);
          background: rgba(16, 32, 51, 0.03);
        }
        
        .check-metadata th {
          background-color: rgba(16, 32, 51, 0.06);
          width: 30%;
          color: #14253a;
          border: 1px solid rgba(20, 37, 58, 0.12);
        }

        .check-metadata td {
          color: #334255;
          border: 1px solid rgba(20, 37, 58, 0.12);
        }

        .check-metadata h4 {
          color: #14253a;
        }
      `}</style>
    </div>
  );
};

export default ClinicReportView; 
